<script setup>
import { reactive, watch } from 'vue'
import { usePrestationStore } from '../../stores/prestationStore'

const props = defineProps({
  prestation: {
    type: Object,
    required: true,
  }
})

const prestationStore = usePrestationStore()
const imageErrors = reactive({ voiture: false, circuit: false })
watch(() => [props.prestation.voiture_img, props.prestation.circuit_img], () => {
  imageErrors.voiture = false
  imageErrors.circuit = false
})

// Modal functions using the store
const openBeneficiaireModal = () => {
  prestationStore.openBeneficiaireModal(props.prestation.id, {
    fullname: props.prestation.beneficiaire_fullname,
    change: props.prestation.beneficiary_change
  })
}

const openDateModal = () => {
  prestationStore.openDateModal(
    props.prestation.id, 
    props.prestation.date_human,
    props.prestation.creneau_human
  )
}

const openToursModal = () => {
  prestationStore.openToursModal(
    props.prestation.id,
    props.prestation.nb_tours
  )
}
</script>

<template>
  <div class="card-prestation">
    <div class="prestation-beneficiaire">
      <p>Bénéficiaire: {{ prestation.beneficiaire_fullname }}</p>
    </div>

    <div class="prestation-body">
      <div class="prestation-items">
        <div class="prestation-item prestation-voiture">
          <div class="prestation-item-content">
            <div class="prestation-image">
              <img v-if="prestation.voiture_img && !imageErrors.voiture"
                   :src="prestation.voiture_img" class="card-img-top" :alt="prestation.voiture_name"
                   loading="lazy" decoding="async" @error="imageErrors.voiture = true">
              <span v-else class="prestation-image-placeholder" role="img" :aria-label="$t('Image indisponible')">
                <i class="bi bi-image" aria-hidden="true"></i>
              </span>
            </div>
            <div class="prestation-info">
              <h5 class="prestation-title">{{ prestation.voiture_marque }}</h5>
              <p class="prestation-subtitle">{{ prestation.voiture_name }}</p>
            </div>
          </div>
        </div>

        <div class="prestation-item prestation-circuit">
          <div class="prestation-item-content">
            <div class="prestation-image">
              <img v-if="prestation.circuit_img && !imageErrors.circuit"
                   :src="prestation.circuit_img" class="card-img-top" :alt="prestation.circuit_name"
                   loading="lazy" decoding="async" @error="imageErrors.circuit = true">
              <span v-else class="prestation-image-placeholder" role="img" :aria-label="$t('Image indisponible')">
                <i class="bi bi-image" aria-hidden="true"></i>
              </span>
            </div>
            <div class="prestation-info">
              <h5 class="prestation-title">{{ prestation.circuit_name }}</h5>
              <p class="prestation-subtitle">{{ prestation.circuit_piste }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="prestation-details">
        <div class="prestation-type-section">
          <div class="prestation-type">
            <div class="prestation-type-content">
              <div
                class="prestation-type-label"
                v-text="prestation.is_bapteme ? 'Baptême passager' : 'Pilotage au volant'"
              ></div>
              <h5 class="prestation-tours">{{ prestation.nb_tours }} Tour{{ prestation.nb_tours > 1 ? 's' : '' }}</h5>
              <div v-if="prestation.nb_tours > prestation.stage?.nb_tour_min" class="prestation-tours-extra">
                <small class="text-muted">
                  + {{ prestation.nb_tours - prestation.stage?.nb_tour_min }} tour{{ (prestation.nb_tours - prestation.stage?.nb_tour_min) > 1 ? 's' : '' }} supplémentaire{{ (prestation.nb_tours - prestation.stage?.nb_tour_min) > 1 ? 's' : '' }}
                </small>
              </div>
            </div>
          </div>
        </div>

        <div class="prestation-validity">
          <div class="prestation-validity-content">
            <span v-if="prestation.date_human">
              {{ prestation.date_human }}
              <div v-if="prestation.creneau_human" v-text="prestation.creneau_human"></div>
            </span>
            <span v-else class="validity-voucher">Bon valable jsuqu'au {{ prestation.valid_until_human }}</span>
          </div>
        </div>
      </div>

      <div v-if="prestation.is_completed || prestation.is_expired" class="modify-section" role="status">
        <h5 class="modify-title">{{ prestation.is_completed ? $t('Déjà effectué') : $t('Bon expiré') }}</h5>
        <p>{{ $t('Une nouvelle commande est nécessaire pour réserver.') }}</p>
      </div>
      <div v-else class="modify-section">
        <h5 class="modify-title">Modifier la prestation</h5>
        <p v-if="prestation.beneficiary_change?.locked">{{ $t('Modification du bénéficiaire : des frais de 49 € s’appliquent.') }}</p>
        <a v-if="prestation.beneficiary_change?.locked" :href="prestation.beneficiary_change.download_url">{{ $t('Télécharger le bon cadeau') }}</a>
        <div class="modify-buttons">
          <a v-if="prestation.beneficiary_change?.locked && prestation.beneficiary_change?.status && prestation.beneficiary_change.status !== 'completed'" :href="prestation.beneficiary_change.payment_url" class="edit-btn">{{ $t('Suivre ma demande de changement') }}</a>
          <button v-else class="edit-btn" :title="prestation.beneficiary_change?.locked ? $t('Demander un changement de bénéficiaire') : $t('Modifier le bénéficiaire')" @click="openBeneficiaireModal">
            <i class="bi bi-people fs-4"></i>
          </button>
          <button class="edit-btn" title="modifier la date" @click="openDateModal">
            <i class="bi bi-calendar fs-4"></i>
          </button>
          <button class="edit-btn" title="ajouter des tours" @click="openToursModal">
            <i class="bi bi-arrow-repeat fs-4"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .edit-btn {
    width: 60px;
    height: 60px;
    border-radius: 10px;
  }
</style>